/**
 * TRAD-3635 — 코인원 공개 WebSocket(wss://stream.coinone.co.kr) 구독 프로토콜. 순수 함수만 둔다(shared-worker 가 쓴다).
 *
 * 실측(2026-10-07 10:46 KST, docs-ops/2026-10-06-coinone-backend-design.md §2 와 같은 결과):
 * - 종목 1개 = SUBSCRIBE 1개. topic 의 target_currency 를 배열로 보내면 `ERROR 160010 Invalid Request`.
 * - 구독은 «누적»이다 — 빗썸·업비트 ticket 처럼 새 프레임이 이전 구독을 대체하지 않는다. 빼려면 UNSUBSCRIBE.
 * - PING 은 JSON `{"request_type":"PING"}` → `{"response_type":"PONG"}`. 문자열 'PING' 은 160010 거부.
 * - 서버는 마지막 PING 후 30분이면 유휴로 끊는다(문서). IP 당 연결 20개 상한 — 워커가 탭을 공유해 연결은 1개다.
 * - SHORT 포맷: DATA 키가 짧다(qc/tc/la/fi…). SUBSCRIBED ack 는 SHORT 여도 긴 형식으로 온다.
 */

const SEP = '|';

/** 내부 키 = `CHANNEL|QUOTE|TARGET`(대문자). 통일 코드 `KRW-BTC` 를 받는다. 형식이 틀리면 null. */
export const toCoinoneTopicKey = (channel: string, code: string): string => {
  const [quote, target] = code.toUpperCase().split('-');
  return [channel.toUpperCase(), quote ?? '', target ?? ''].join(SEP);
};

const isValidKey = (key: string): boolean => {
  const [channel, quote, target] = key.split(SEP);
  return Boolean(channel && quote && target);
};

export const coinoneRequest = (requestType: 'SUBSCRIBE' | 'UNSUBSCRIBE', key: string): string => {
  const [channel, quote, target] = key.split(SEP);
  const base = { request_type: requestType, channel, topic: { quote_currency: quote, target_currency: target } };
  return JSON.stringify(requestType === 'SUBSCRIBE' ? { ...base, format: 'SHORT' } : base);
};

/**
 * 지금 연결에 구독된 것(prev)과 원하는 것(codesByType: subType → 통일 코드 집합)의 차이.
 * 누적 프로토콜이라 «차이만» 보낸다 — 이미 구독된 종목을 다시 SUBSCRIBE 하지 않고, 빠진 것만 UNSUBSCRIBE.
 * 재연결 직후에는 prev 가 비어 있어 전부 SUBSCRIBE 가 된다(호출부가 새 연결에서 prev 를 비운다).
 */
export const diffCoinoneSubscriptions = (
  prev: ReadonlySet<string>,
  codesByType: ReadonlyMap<string, ReadonlySet<string>>,
): { subscribe: string[]; unsubscribe: string[]; next: Set<string> } => {
  const next = new Set<string>();
  codesByType.forEach((codes, subType) => {
    codes.forEach(code => {
      const key = toCoinoneTopicKey(subType, code);
      if (isValidKey(key)) next.add(key);
    });
  });
  return {
    subscribe: [...next].filter(k => !prev.has(k)),
    unsubscribe: [...prev].filter(k => !next.has(k)),
    next,
  };
};

export const COINONE_PING = JSON.stringify({ request_type: 'PING' });

export const isCoinonePong = (text: string): boolean => {
  try {
    const parsed: unknown = JSON.parse(text);
    return typeof parsed === 'object' && parsed !== null && (parsed as { response_type?: unknown }).response_type === 'PONG';
  } catch {
    return false;
  }
};
